import { effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { EventType } from '@ag-ui/core';
import type { BaseEvent, Message, RunAgentInput, Tool, ToolCallEndEvent } from '@ag-ui/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import type { BaseMetaState } from '@core/ngrx-legacy';
import type { FrontendTool } from '@core/tellent-assistant-contracts';
import { injectTellentFrontendTools } from '@core/tellent-assistant-contracts';
import {
  TellentAssistantAguiClient,
  TellentAssistantCurrentThreadActions,
  TellentAssistantCurrentThreadSelectors,
  TellentAssistantThreadsActions,
  TellentAssistantThreadsSelectors,
  TellentAssistantWorkflowHttp,
} from '@core/tellent-assistant-data';
import type { ThreadSummary } from '@core/tellent-assistant-types';

import { TellentAssistantAppContextService } from './tellent-assistant-app-context.service';
import { TellentAssistantEventAdapter } from './tellent-assistant-event-adapter';
import { TellentAssistantStore } from './tellent-assistant.store';

@Injectable({ providedIn: 'root' })
export class TellentAssistantFacade {
  private readonly store = inject(TellentAssistantStore);
  private readonly adapter = inject(TellentAssistantEventAdapter);
  private readonly client = inject(TellentAssistantAguiClient);
  private readonly workflowHttp = inject(TellentAssistantWorkflowHttp);
  private readonly appContext = inject(TellentAssistantAppContextService);
  private readonly frontendTools = injectTellentFrontendTools({ optional: true }) ?? [];
  private toolCallDepth = 0;
  private static readonly MAX_TOOL_DEPTH = 8;
  private readonly store$ = inject(Store);
  private readonly threadMetaMap = toSignal(
    this.store$.select(TellentAssistantThreadsSelectors.entityMetaMap),
    { initialValue: <Record<string, BaseMetaState>>{} },
  );

  public readonly messages;
  public readonly runStatus;
  public readonly pendingInterrupt;
  public readonly sharedState;
  public readonly threads;
  public readonly currentThreadId;
  public readonly lastError;
  public readonly contextSnapshot = toSignal(this.appContext.changes$, {
    initialValue: this.appContext.snapshot(),
  });

  private activeRun: Subscription | null = null;

  constructor() {
    this.messages = this.store.messages.asReadonly();
    this.runStatus = this.store.runStatus.asReadonly();
    this.pendingInterrupt = this.store.pendingInterrupt.asReadonly();
    this.sharedState = this.store.sharedState.asReadonly();
    this.threads = toSignal(this.store$.select(TellentAssistantThreadsSelectors.allEntities), {
      initialValue: <ThreadSummary[]>[],
    });
    this.currentThreadId = this.store.currentThreadId.asReadonly();
    this.lastError = this.store.lastError.asReadonly();

    const currentThread = toSignal(
      this.store$.select(TellentAssistantCurrentThreadSelectors.data),
      { initialValue: null },
    );

    effect(() => {
      const data = currentThread();
      if (!data) return;
      if (data.threadId !== this.store.currentThreadId()) return;
      this.store.replaceMessages(data.messages);
    });
  }

  public send(text: string): void {
    this.toolCallDepth = 0;
    if (!this.store.currentThreadId()) this.store.newThread(crypto.randomUUID());
    this.store.appendUserMessage(text);
    this.invoke();
  }

  public newThread(): void {
    this.cancelRun();
    this.store.newThread(crypto.randomUUID());
  }

  public switchThread(threadId: string): void {
    this.cancelRun();
    this.store.newThread(threadId);
    this.store$.dispatch(TellentAssistantCurrentThreadActions.fetch({ payload: { threadId } }));
  }

  public loadThreads(): void {
    this.store$.dispatch(TellentAssistantThreadsActions.fetchCollection());
  }

  public deleteThread(threadId: string): void {
    this.store$.dispatch(
      TellentAssistantThreadsActions.removeOnePessimistic({ payload: { data: threadId } }),
    );
  }

  public isThreadDeleting(threadId: string): boolean {
    const meta = this.threadMetaMap()[threadId];
    return meta?.pending ?? false;
  }

  public resolveInterrupt(interruptId: string, payload: unknown): void {
    const pending = this.store.pendingInterrupt();
    if (!pending || pending.id !== interruptId) return;
    this.store.markInterruptResolved(interruptId);

    if (pending.resume.kind === 'workflow') {
      this.workflowHttp
        .resume(pending.resume.workflowId, pending.resume.runId, pending.resume.stepName, payload)
        .subscribe({
          error: err =>
            this.store.markRunError(err instanceof Error ? err.message : 'workflow_resume_error'),
        });
      return;
    }

    this.invoke({ kind: 'agent', interruptId, payload });
  }

  public cancelRun(): void {
    this.activeRun?.unsubscribe();
    this.activeRun = null;
  }

  private invoke(resume?: { kind: 'agent'; interruptId: string; payload: unknown }): void {
    const input = <RunAgentInput>{
      threadId: this.store.currentThreadId()!,
      runId: crypto.randomUUID(),
      messages: this.toAguiMessages(),
      tools: this.toAguiTools(),
      context: [],
      state: { appContext: this.appContext.snapshot() },
      ...(resume ? { forwardedProps: { resume } } : {}),
    };

    this.cancelRun();
    this.activeRun = this.client.run(input).subscribe({
      next: event => {
        this.adapter.apply(event, this.store);
        this.onToolEvent(event);
      },
      error: err => this.store.markRunError(err instanceof Error ? err.message : 'unknown_error'),
      complete: () => {
        this.activeRun = null;
      },
    });
  }

  private toAguiMessages(): Message[] {
    const out: Message[] = [];
    for (const m of this.store.messages()) {
      if (m.role === 'user') {
        const text = m.parts.find(p => p.kind === 'text');
        if (text && text.kind === 'text') {
          out.push(<Message>{ id: m.id, role: <const>'user', content: text.content });
        }
        continue;
      }
      const textPart = m.parts.find(p => p.kind === 'text');
      if (textPart && textPart.kind === 'text') {
        out.push(<Message>{ id: m.id, role: <const>'assistant', content: textPart.content });
      }
      for (const p of m.parts) {
        if (p.kind === 'tool-result') {
          out.push(<Message>{
            id: crypto.randomUUID(),
            role: <const>'tool',
            toolCallId: p.toolCallId,
            content: typeof p.payload === 'string' ? p.payload : JSON.stringify(p.payload),
          });
        }
      }
    }
    return out;
  }

  private toAguiTools(): Tool[] {
    return <Tool[]>this.frontendTools.map(t => ({
      name: t.name,
      description: t.description,
      parameters: t.parameters,
    }));
  }

  private onToolEvent(event: BaseEvent): void {
    if (event.type !== EventType.TOOL_CALL_END) return;
    const e = <ToolCallEndEvent>event;
    const call = this.findToolCall(e.toolCallId);
    if (!call) return;
    const match = this.frontendTools.find(t => t.name === call.name);
    if (!match) return;
    void this.handleToolCall(e.toolCallId, match, call.args);
  }

  private findToolCall(toolCallId: string): { name: string; args: string } | null {
    for (const m of this.store.messages()) {
      for (const p of m.parts) {
        if (p.kind === 'tool-call' && p.toolCallId === toolCallId) {
          return { name: p.name, args: typeof p.args === 'string' ? p.args : '' };
        }
      }
    }
    return null;
  }

  private async handleToolCall(
    toolCallId: string,
    tool: FrontendTool<unknown, unknown>,
    rawArgs: string,
  ): Promise<void> {
    this.toolCallDepth += 1;
    if (this.toolCallDepth > TellentAssistantFacade.MAX_TOOL_DEPTH) {
      this.toolCallDepth = 0;
      this.store.appendToolResult(
        toolCallId,
        JSON.stringify({ error: 'tool_call_depth_exceeded' }),
      );
      this.store.markRunError('tool call depth limit exceeded');
      return;
    }
    const msg = await this.runTool(toolCallId, tool, rawArgs);
    const content = msg.error !== undefined ? JSON.stringify({ error: msg.error }) : msg.content;
    this.store.appendToolResult(toolCallId, content);
    this.invoke();
  }

  private async runTool(
    toolCallId: string,
    tool: FrontendTool<unknown, unknown>,
    rawArgs: string,
  ): Promise<{ content: string; error?: string }> {
    let parsedArgs: unknown;
    try {
      parsedArgs = JSON.parse(rawArgs || '{}');
    } catch {
      return this.toolError('invalid args: not JSON');
    }
    const input = tool.input.safeParse(parsedArgs);
    if (!input.success) {
      return this.toolError('invalid args: ' + input.error.message);
    }
    let output: unknown;
    try {
      output = await tool.execute(input.data);
    } catch (e) {
      return this.toolError(e instanceof Error ? e.message : 'tool execution failed');
    }
    const outputParse = tool.output.safeParse(output);
    if (!outputParse.success) {
      return this.toolError('invalid output: ' + outputParse.error.message);
    }
    return { content: JSON.stringify(outputParse.data) };
  }

  private toolError(error: string): { content: string; error: string } {
    return { content: '', error };
  }
}
